<template>

  <TestTitle :display-price="false"
             v-if="currentInstruction.name  === 'generic'|| currentInstruction.name === 'custom'"></TestTitle>
  <MonitorInstructionTitle :current-page="currentInstruction.name" :display-price="false" v-else>

  </MonitorInstructionTitle>


  <GenericInstructions
      v-if="currentInstruction.name === 'generic'"
      @complete="onInstructionComplete"/>

  <CustomInstructions
      v-if="currentInstruction.name === 'custom'"
      @complete="onInstructionComplete"/>

  <BrowserInstructions
      v-if="currentInstruction.name === 'browser'"
      @complete="onInstructionComplete"/>

  <CameraInstructions
      v-if="currentInstruction.name === 'camera'"
      ref="cameraInstruction"
      @complete="onInstructionComplete"
      @cancel="onInstructionCancel"
  />

  <div class="button-area">
    <ion-button
        class="ion-float-left"
        data-cy="previous-btn"
        @click="previousButtonClick"
        v-if="previousButtonVisible"
        :disabled="!previousButtonEnabled">
      {{ previousButtonLabel }}
    </ion-button>

    <ion-button
        class="ion-float-right"
        data-cy="continue-btn"
        @click="nextButtonClick"
        v-if="nextButtonVisible && currentInstruction.name !== 'camera' "
        :disabled="!nextButtonEnabled">
      {{ nextButtonLabel }}
    </ion-button>

    <ion-button
        v-else-if="nextButtonVisible && currentInstruction.name === 'camera' "
        class="ion-float-right"
        data-cy="continue-btn"
        @click="nextButtonClick"
        v-tooltip="{location: 'top'}"
        :data-title="language['camera_monitor_test_title']"
        :class="{'disabled' : !nextButtonEnabled}">
      {{ nextButtonLabel }}
    </ion-button>

  </div>

  <ion-loading :isOpen="loading" message=""
               mode="ios"

               spinner="dots"></ion-loading>
</template>

<script setup>
import {computed, getCurrentInstance, onMounted, reactive, ref} from 'vue';
import {useRouter} from 'vue-router';
import {useStore} from 'vuex';
import {IonButton, IonLoading} from '@ionic/vue';
import GenericInstructions from '@/components/test-intro/instructions/GenericInstructions.vue';
import CustomInstructions from '@/components/test-intro/instructions/CustomInstructions.vue';
import BrowserInstructions from '@/components/test-intro/instructions/BrowserInstructions.vue';
import CameraInstructions from '@/components/test-intro/instructions/CameraInstructions.vue';
import RouteHandler from '@/router/routeHandler';
import constant from "@/constant";
import TestTitle from "@/components/html/TestTitle.vue";
import MonitorInstructionTitle from "@/components/test-intro/instructions/MonitorInstructionTitle.vue"

const store = useStore();
const loading = ref(false);
const data = reactive(store.state.Test.data['test_intro']);
const settings = reactive(store.state.Test.settings['test']);
const language = reactive(store.getters.languageSet);

let uploadImageCheckerId = null;

const routeHandler = new RouteHandler(useRouter());
const internalInstance = getCurrentInstance();
let globalEmitter = internalInstance.appContext.config.globalProperties.$globalEmitter


const cameraInstruction = ref(null);

const instructions = [
  {
    name: 'generic',
    order: 1,
    isComplete: ref(false),
    isAvailable: computed(() => {
       let default_availability =  settings['display_pretest_instruction'] === true ||
          (data['test_instruction'] !== null && data['test_instruction'] !== '');
       let other_availability =  settings['display_pretest_custom_instruction'] === true
           || store.getters['Monitoring/isMonitoringBrowserEnabled']
           || store.getters['Monitoring/isMonitoringCameraEnabled'];
       return default_availability || (default_availability===false && other_availability === false);
    }),
  },
  {
    name: 'custom',
    order: 2,
    isComplete: ref(false),
    isAvailable: computed(() => {
      return data['display_pretest_custom_instruction'] === true;
    })
  },
  {
    name: 'browser',
    order: 3,
    isComplete: ref(false),
    isAvailable: computed(() => {
      return store.getters['Monitoring/isMonitoringBrowserEnabled'];
    })
  },
  {
    name: 'camera',
    order: 4,
    isComplete: ref(false),
    isAvailable: computed(() => {
      return store.getters['Monitoring/isMonitoringCameraEnabled'];
    })
  }
];


const availableInstructions = computed(() => {
  return instructions
      .filter(instruction => instruction.isAvailable.value)
      .sort((a, b) => a - b);
});


const currentInstructionIndex = ref(0);
const currentInstruction = computed(() => {
  return availableInstructions.value[currentInstructionIndex.value];
});


const onInstructionComplete = (value=true) => {
  currentInstruction.value.isComplete.value = value;
};

const onInstructionCancel = () => {
  currentInstruction.value.isComplete.value = false;
};

const previousButtonLabel = computed(() => {
  return language['link_previous'];
});
const previousButtonEnabled = computed(() => {
  return !loading.value;
});
const previousButtonVisible = computed(() => {
  if (availableInstructions.value.length > 1 && currentInstructionIndex.value > 0) {
    return true;
  }
  const loginSettings = store.getters['loginSetting'];
  return !!(loginSettings['first_name_required'] ||
      loginSettings['last_name_required'] ||
      loginSettings['email_required'] ||
      loginSettings['user_extra_info_required']);
});
const previousButtonClick = () => {
  if(currentInstruction.value.name === 'camera'){
    globalEmitter.emit('stop-camera');
  }
  if (currentInstructionIndex.value > 0) {
    currentInstructionIndex.value--;
  } else {
    routeHandler.goBack();
  }
};



const nextButtonLabel = computed(() => {
  return currentInstructionIndex.value + 1 === availableInstructions.value.length ?
      language['link_start_test'] : language['link_continue'];
});


const nextButtonEnabled = computed(() => {
  return !loading.value && currentInstruction.value.isComplete.value;
});


const nextButtonVisible = computed(() => {
  return true;
});

const startTest = () => {
  if (store.getters["Monitoring/isMonitoringBrowserEnabled"]) {
    store.dispatch('Monitoring/setUserStartBrowserMonitoring', true)
    globalEmitter.emit('start-browser-monitoring');
  }


  store.dispatch('Test/startTest').then(() => {
    if (store.getters["Monitoring/isMonitoringCameraEnabled"]) {
      store.dispatch('Monitoring/setCameraMonitoringStart', true);
    }

    routeHandler.route();
  }).catch((error) => {

    if (store.state.Error.error.error_code === constant.ERROR_NO_INTERNET) {
      store.dispatch('Error/openModal');
    } else {
      store.dispatch('Error/displayFromAPIError', error);
    }
  }).finally(
      () => {
        loading.value = false
      });
};


globalEmitter.on('error-on-intro-photo-uploading', (error)=>{
  store.dispatch('Monitoring/setUserStartBrowserMonitoring', false)
  globalEmitter.emit('stop-browser-monitoring');
  loading.value = false
  store.dispatch('Error/displayFromAPIError', error);

  //Clear interval to ensure we only start one test.
  clearInterval(uploadImageCheckerId);
  uploadImageCheckerId = null;

})


const nextButtonClick = (event) => {

  // check if button is disabled
  // This function used only for the tooltip (not working with ionic default disabled function)
  let classList=event.target.classList;
  for (let i = classList.length - 1; i >= 0; i--) {
    if(classList[i]==='disabled'){
      event.preventDefault();
      return
    }
  }


  loading.value = true;
  // button actions when each instruction clicked
  switch (currentInstruction.value.name) {

    case 'camera': //camera is the last one now
      let image = cameraInstruction.value.capturedImage;

      // trigger upload intro image
      globalEmitter.emit(constant.EVENT_SUBMIT_PHOTO, {type: constant.PHOTO_INTRO, image: image});

      uploadImageCheckerId = setInterval(() => {
        if (store.state.Monitoring.introImageUploaded === true) {
          clearInterval(uploadImageCheckerId)
          startTest();
        }
      }, 500)
      break;

    default:

      if (currentInstructionIndex.value + 1 === availableInstructions.value.length) {
        startTest();
      } else {
        currentInstructionIndex.value++;
        loading.value = false
      }
  }
};


</script>

<style lang="scss" scoped>


:deep(.button-area) {
  margin-top: 0;
}


</style>